import { useCallback, useEffect, useRef, useState } from 'react';
import { HallMap, type HallMapProps } from './HallMap';

/**
 * The hall map with zoom, pan and a fullscreen mode.
 *
 * The hall is 148 feet wide with 99 tables on it. Fitted to a column it is
 * legible as a picture but useless as a control — you cannot tell table 58
 * from 59, let alone tap one on a phone. So panning and zooming are not a
 * nicety here, they are what makes the map usable at all.
 *
 * Zoom moves the SVG viewBox rather than applying a CSS transform. Strokes and
 * text stay crisp at every scale, and hit targets stay exactly where they are
 * drawn — which matters a great deal once a vendor is picking a specific table.
 */

export interface HallMapViewerProps extends Omit<HallMapProps, 'view'> {
  /** Shown above the controls, e.g. "Bingo Hall". */
  caption?: string;
  /**
   * A bar rendered inside the fullscreen view.
   *
   * Fullscreen takes over the whole screen, which means anything outside this
   * component — a cart, a Continue button — is simply gone. Without somewhere
   * to carry on, a vendor who zoomed in to read the table numbers has to work
   * out that they must first leave fullscreen, which is a dead end dressed up
   * as a feature.
   *
   * Receives `exit` so the action can close fullscreen before moving the
   * vendor to a step that lives outside this component.
   */
  fullscreenActions?: (exit: () => void) => React.ReactNode;
  /** True bounds of the artwork; defaults to the whole coordinate space. */
  content?: { x: number; y: number; w: number; h: number };
}

interface View {
  x: number;
  y: number;
  w: number;
  h: number;
}

const MAX_ZOOM = 10;
/**
 * How far the pointer may stray, in px, and still count as a click.
 *
 * Measured as straight-line distance from where the pointer went DOWN, not as
 * accumulated path length. Summing every move's delta counts jitter twice
 * over: a perfectly ordinary click emits several move events of a pixel or
 * two, the total sailed past a small threshold, and the click was discarded as
 * a drag — so tapping a table panned the map instead of selecting it.
 *
 * 8px is roughly what browsers themselves allow between mousedown and mouseup
 * before they stop calling it a click.
 */
const DRAG_SLOP = 8;

export function HallMapViewer({
  caption,
  fullscreenActions,
  content,
  ...map
}: HallMapViewerProps) {
  // Fit to where the drawing really is, not to the coordinate space it was
  // authored in — those differ in the club's export, and the gap reads as a
  // page that is off-centre.
  const full: View = content ?? { x: 0, y: 0, w: map.width, h: map.height };
  const [view, setView] = useState<View>(full);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const shellRef = useRef<HTMLDivElement>(null);
  const svgWrapRef = useRef<HTMLDivElement>(null);

  // Pointer bookkeeping for pan and pinch, kept in a ref so the wheel and move
  // handlers do not re-subscribe on every frame.
  const gesture = useRef({
    pointers: new Map<number, { x: number; y: number }>(),
    panning: false,
    moved: 0,
    /** Where this gesture began, for the straight-line test above. */
    origin: { x: 0, y: 0 },
    last: { x: 0, y: 0 },
    startSpread: 0,
    startView: full,
  });

  const clamp = useCallback(
    (v: View): View => {
      const minW = full.w / MAX_ZOOM;
      const w = Math.min(full.w, Math.max(minW, v.w));
      const h = w * (full.h / full.w);
      // The window is pinned inside the hall. Allowing it to overhang meant
      // that at full zoom the drawing could sit off-centre with dead space
      // beside it, and the tables along the far wall fell outside the frame.
      // At w === map.width both bounds collapse to 0, which is exactly right:
      // fitted means fitted.
      const x = Math.min(Math.max(v.x, full.x), Math.max(full.x, full.x + full.w - w));
      const y = Math.min(Math.max(v.y, full.y), Math.max(full.y, full.y + full.h - h));
      return { x, y, w, h };
    },
    [full.x, full.y, full.w, full.h],
  );

  /** Zoom by `factor` about a point given in client coordinates. */
  const zoomAbout = useCallback(
    (factor: number, clientX?: number, clientY?: number) => {
      setView((prev) => {
        const el = svgWrapRef.current;
        let fx = 0.5;
        let fy = 0.5;
        if (el && clientX !== undefined && clientY !== undefined) {
          const r = el.getBoundingClientRect();
          fx = (clientX - r.left) / r.width;
          fy = (clientY - r.top) / r.height;
        }
        const anchorX = prev.x + prev.w * fx;
        const anchorY = prev.y + prev.h * fy;
        const w = prev.w / factor;
        const h = prev.h / factor;
        return clamp({ x: anchorX - w * fx, y: anchorY - h * fy, w, h });
      });
    },
    [clamp],
  );

  const reset = useCallback(
    () => setView(full),
    [full.x, full.y, full.w, full.h],
  );

  // Wheel zoom, but only for a deliberate zoom gesture.
  //
  // A plain wheel or two-finger swipe must keep scrolling the page. This map
  // is embedded in the middle of the club's own site, and a map that swallows
  // scrolling traps anyone trying to read past it — on a laptop trackpad that
  // is close to unescapable.
  //
  // A trackpad pinch arrives as a wheel event with ctrlKey set, which is how
  // browsers have signalled "zoom, do not scroll" since long before this app.
  // Registered manually because React's onWheel is passive and so cannot
  // preventDefault.
  useEffect(() => {
    const el = svgWrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return; // let the page scroll
      e.preventDefault();
      zoomAbout(e.deltaY < 0 ? 1.15 : 1 / 1.15, e.clientX, e.clientY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAbout]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // In the CSS fallback the browser never entered real fullscreen, so its own
  // Escape handling does not apply and the vendor would be stuck.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isFullscreen]);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    }
    // Also clears the CSS fallback used when the browser refused the request.
    setIsFullscreen(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;

    // Check our own state, not just document.fullscreenElement.
    //
    // When the browser refuses the fullscreen request — an iframe without
    // allow="fullscreen" is the usual reason — we expand with a CSS class
    // instead. In that state there IS no fullscreenElement, so a check
    // against it alone concluded we were not fullscreen and tried to enter
    // again. The request rejected, the catch re-set the flag, and the button
    // labelled "Exit full screen" did nothing at all: a trap on exactly the
    // path least able to afford one.
    if (document.fullscreenElement || isFullscreen) {
      exitFullscreen();
      return;
    }

    if (el.requestFullscreen) {
      el.requestFullscreen().catch(() => setIsFullscreen(true));
    } else {
      setIsFullscreen(true);
    }
  }, [isFullscreen, exitFullscreen]);

  const onPointerDown = (e: React.PointerEvent) => {
    const g = gesture.current;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    g.moved = 0;
    g.origin = { x: e.clientX, y: e.clientY };
    g.startView = view;
    if (g.pointers.size === 1) {
      g.panning = true;
      g.last = { x: e.clientX, y: e.clientY };
    } else if (g.pointers.size === 2) {
      g.panning = false;
      g.startSpread = spread(g.pointers);
    }
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const g = gesture.current;
    if (!g.pointers.has(e.pointerId)) return;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (g.pointers.size === 2) {
      const now = spread(g.pointers);
      if (g.startSpread > 0) {
        const el = svgWrapRef.current;
        const r = el?.getBoundingClientRect();
        const mid = midpoint(g.pointers);
        setView(
          clamp(scaleAbout(g.startView, now / g.startSpread, mid, r)),
        );
      }
      return;
    }

    if (!g.panning) return;

    // Track displacement even when the map cannot pan. Skipping this while
    // fitted left `moved` at zero, so a drag right across the hall still
    // counted as a click and toggled whatever table it happened to end on —
    // which on a phone is any horizontal swipe over the map.
    g.moved = Math.hypot(e.clientX - g.origin.x, e.clientY - g.origin.y);

    const dx = e.clientX - g.last.x;
    const dy = e.clientY - g.last.y;
    g.last = { x: e.clientX, y: e.clientY };

    if (view.w >= full.w - 1) return; // fitted: measured, but nothing to pan

    const el = svgWrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setView((prev) =>
      clamp({
        ...prev,
        x: prev.x - (dx / r.width) * prev.w,
        y: prev.y - (dy / r.height) * prev.h,
      }),
    );
  };

  const endPointer = (e: React.PointerEvent) => {
    const g = gesture.current;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size === 0) g.panning = false;
    if (g.pointers.size < 2) g.startSpread = 0;
  };

  // A gesture that travelled is a pan, not a table click. Swallowing it in the
  // capture phase stops a vendor selecting a table they merely dragged across.
  const onClickCapture = (e: React.MouseEvent) => {
    if (gesture.current.moved > DRAG_SLOP) {
      e.stopPropagation();
      e.preventDefault();
      gesture.current.moved = 0;
    }
  };

  const zoomed = view.w < full.w - 1;

  return (
    <div
      ref={shellRef}
      className={`hallmapv${isFullscreen ? ' is-fullscreen' : ''}`}
    >
      <div className="hallmapv__bar">
        {caption && <span className="hallmapv__caption">{caption}</span>}
        <div className="hallmapv__controls">
          <button type="button" onClick={() => zoomAbout(1.4)} aria-label="Zoom in">
            +
          </button>
          <button type="button" onClick={() => zoomAbout(1 / 1.4)} aria-label="Zoom out">
            &minus;
          </button>
          <button type="button" onClick={reset} disabled={!zoomed}>
            Fit
          </button>
          <button type="button" onClick={toggleFullscreen}>
            {isFullscreen ? 'Exit full screen' : 'Full screen'}
          </button>
        </div>
      </div>

      <div
        ref={svgWrapRef}
        className={`hallmapv__stage${zoomed ? ' is-zoomed' : ''}`}
        // Fitted: keep vertical swipes with the browser so the page can always
        // be scrolled away from the map. Zoomed: take every gesture, because
        // dragging now means moving the map, and "Fit" is the way back.
        style={{ touchAction: zoomed || isFullscreen ? 'none' : 'pan-y' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
        onClickCapture={onClickCapture}
      >
        <HallMap {...map} view={view} />
      </div>

      {isFullscreen && fullscreenActions && (
        <div className="hallmapv__dock">{fullscreenActions(exitFullscreen)}</div>
      )}

      <p className="hallmapv__hint">
        Pinch or use + and &minus; to zoom, drag to move.{' '}
        {zoomed ? 'Press Fit to see the whole hall.' : ''}
      </p>
    </div>
  );
}

function spread(pointers: Map<number, { x: number; y: number }>): number {
  const [a, b] = [...pointers.values()];
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function midpoint(pointers: Map<number, { x: number; y: number }>) {
  const [a, b] = [...pointers.values()];
  if (!a || !b) return { x: 0, y: 0 };
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

function scaleAbout(
  start: View,
  factor: number,
  clientPoint: { x: number; y: number },
  rect: DOMRect | undefined,
): View {
  const fx = rect ? (clientPoint.x - rect.left) / rect.width : 0.5;
  const fy = rect ? (clientPoint.y - rect.top) / rect.height : 0.5;
  const anchorX = start.x + start.w * fx;
  const anchorY = start.y + start.h * fy;
  const w = start.w / factor;
  const h = start.h / factor;
  return { x: anchorX - w * fx, y: anchorY - h * fy, w, h };
}
